import { dictionaryApi } from './dictionaryClient';
import { normalizeBartikPadachhed, type BartikPadachhed } from './ashtadhyayiBartikPadachhedApi';
import {
  normalizeBartikAdhikar,
  type BartikAdhikar,
} from './ashtadhyayiBartikAdhikarApi';
import {
  normalizeBartikAnubritti,
  type BartikAnubritti,
} from './ashtadhyayiBartikAnubrittiApi';

export type { BartikPadachhed };
export type { BartikAnubritti, BartikAdhikar };

export type AshtadhyayiBartik = {
  id: number;
  ashtadhyayiSutraId: number;
  order: number;
  text: string;
  padachheds: BartikPadachhed[];
  anubrittis: BartikAnubritti[];
  adhikars: BartikAdhikar[];
};

export type CreateAshtadhyayiBartikBody = {
  ashtadhyayiSutraId: number;
  order: number;
  text: string;
};

export type UpdateAshtadhyayiBartikBody = Partial<CreateAshtadhyayiBartikBody>;

export type AshtadhyayiBartikImportRow = {
  i: string;
  vartika: string;
};

function isRecord(x: unknown): x is Record<string, unknown> {
  return x != null && typeof x === 'object' && !Array.isArray(x);
}

function unwrapEntityPayload(raw: unknown): unknown {
  if (isRecord(raw) && 'data' in raw) return raw.data;
  return raw;
}

function num(o: Record<string, unknown>, key: string): number | null {
  const v = o[key];
  if (typeof v === 'number' && Number.isFinite(v)) return v;
  if (typeof v === 'string' && v.trim() !== '' && Number.isFinite(Number(v))) return Number(v);
  return null;
}

function str(o: Record<string, unknown>, key: string): string {
  if (!(key in o) || o[key] == null) return '';
  return String(o[key]);
}

export function normalizeAshtadhyayiBartik(raw: unknown): AshtadhyayiBartik | null {
  if (!isRecord(raw)) return null;
  const id = num(raw, 'id');
  const ashtadhyayiSutraId = num(raw, 'ashtadhyayiSutraId') ?? num(raw, 'ashtadhyayi_sutra_id');
  const order = num(raw, 'order');
  if (id == null || ashtadhyayiSutraId == null || order == null) return null;
  const padachhedsRaw = raw.padachheds ?? raw.padaccheds;
  const padachheds = Array.isArray(padachhedsRaw)
    ? padachhedsRaw
        .map(normalizeBartikPadachhed)
        .filter((x): x is BartikPadachhed => x != null)
        .sort((a, b) => a.order - b.order)
    : [];
  const anubrittisRaw = raw.anubrittis ?? raw.anubritti;
  const anubrittis = Array.isArray(anubrittisRaw)
    ? anubrittisRaw
        .map(normalizeBartikAnubritti)
        .filter((x): x is BartikAnubritti => x != null)
        .sort((a, b) => a.order - b.order)
    : [];
  const adhikarsRaw = raw.adhikars ?? raw.adhikar ?? raw.adhikāras;
  const adhikars = Array.isArray(adhikarsRaw)
    ? adhikarsRaw
        .map(normalizeBartikAdhikar)
        .filter((x): x is BartikAdhikar => x != null)
        .sort((a, b) => a.order - b.order)
    : [];
  return {
    id,
    ashtadhyayiSutraId,
    order,
    text: str(raw, 'text') || str(raw, 'vartika'),
    padachheds,
    anubrittis,
    adhikars,
  };
}

function normalizeList(data: unknown): AshtadhyayiBartik[] {
  const rows =
    Array.isArray(data) ? data : isRecord(data) && Array.isArray(data.data) ? (data.data as unknown[]) : [];
  return rows
    .map(normalizeAshtadhyayiBartik)
    .filter((x): x is AshtadhyayiBartik => x != null)
    .sort((a, b) => a.order - b.order);
}

export async function listAshtadhyayiBartik(ashtadhyayiSutraId?: number): Promise<AshtadhyayiBartik[]> {
  const params =
    ashtadhyayiSutraId != null && Number.isFinite(ashtadhyayiSutraId)
      ? { ashtadhyayiSutraId }
      : undefined;
  const res = await dictionaryApi.get<unknown>('/ashtadhyayi-bartik', { params });
  return normalizeList(res.data);
}

export async function getAshtadhyayiBartik(id: number): Promise<AshtadhyayiBartik> {
  const res = await dictionaryApi.get<unknown>(`/ashtadhyayi-bartik/${id}`);
  const row = normalizeAshtadhyayiBartik(unwrapEntityPayload(res.data));
  if (!row) throw new Error('Invalid ashtadhyayi bartik response');
  return row;
}

export async function createAshtadhyayiBartik(body: CreateAshtadhyayiBartikBody): Promise<AshtadhyayiBartik> {
  const res = await dictionaryApi.post<unknown>('/ashtadhyayi-bartik', body);
  const row = normalizeAshtadhyayiBartik(unwrapEntityPayload(res.data));
  if (!row) throw new Error('Invalid ashtadhyayi bartik response');
  return row;
}

export async function updateAshtadhyayiBartik(
  id: number,
  body: UpdateAshtadhyayiBartikBody
): Promise<AshtadhyayiBartik> {
  const res = await dictionaryApi.patch<unknown>(`/ashtadhyayi-bartik/${id}`, body);
  const row = normalizeAshtadhyayiBartik(unwrapEntityPayload(res.data));
  if (!row) throw new Error('Invalid ashtadhyayi bartik response');
  return row;
}

export async function deleteAshtadhyayiBartik(id: number): Promise<void> {
  await dictionaryApi.delete(`/ashtadhyayi-bartik/${id}`);
}

export async function importAshtadhyayiBartik(data: AshtadhyayiBartikImportRow[]): Promise<void> {
  await dictionaryApi.post('/ashtadhyayi-bartik/import', { data });
}
